import { useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import './Contact.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    website: '', // honeypot — must stay empty
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitStatus, setSubmitStatus] = useState(''); // 'success' | 'error'
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitStatus('');

    try {
      const payload = {
        name: formData.name.trim(),
        company: formData.company.trim() || undefined,
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
        message: formData.message.trim(),
        website: formData.website, // honeypot
        turnstile_token: turnstileToken || undefined,
      };

      const res = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setSubmitMessage('お問い合わせありがとうございます。後ほど担当者からご連絡いたします。');
        setFormData({ name: '', company: '', email: '', phone: '', message: '', website: '' });
        setTurnstileToken('');
        turnstileRef.current?.reset();
        // GA4 conversion event (recommended lead event)
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'generate_lead', {
            event_category: 'contact',
            event_label: 'contact_form',
          });
        }
      } else if (res.status === 422) {
        setSubmitStatus('error');
        setSubmitMessage('入力内容をご確認ください。メールアドレスの形式が正しくない可能性があります。');
      } else if (res.status === 400 || res.status === 429) {
        // Spam-protection rejections carry a specific Japanese message
        let detail = '';
        try {
          detail = (await res.json()).detail || '';
        } catch (_) { /* keep generic */ }
        setSubmitStatus('error');
        setSubmitMessage(detail || '送信に失敗しました。時間をおいて再度お試しください。');
        turnstileRef.current?.reset();
        setTurnstileToken('');
      } else {
        setSubmitStatus('error');
        setSubmitMessage('送信に失敗しました。時間をおいて再度お試しください。');
      }
    } catch (err) {
      setSubmitStatus('error');
      setSubmitMessage('通信エラーが発生しました。ネットワーク環境をご確認のうえ再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <section className="page-hero">
        <div className="container">
          <h1>お問い合わせ</h1>
          <p>製品選び・お見積り・現場確認のご相談など、お気軽にお問い合わせください。</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="contact-container">
            <div className="contact-info">
              <h2>お問い合わせフォーム</h2>
              <p>
                以下のフォームに必要事項をご記入の上、送信してください。
                担当者より折り返しご連絡いたします。
              </p>
            </div>

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">お名前<span className="required">*</span></label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="山田 太郎"
                />
              </div>

              <div className="form-group">
                <label htmlFor="company">会社名</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="株式会社例"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">メールアドレス<span className="required">*</span></label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="example@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">電話番号</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="03-1234-5678"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">ご相談内容<span className="required">*</span></label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  placeholder="ご相談内容をご記入ください。"
                ></textarea>
              </div>

              {/* Honeypot — invisible to humans; bots that fill it are rejected */}
              <div className="hp-field" aria-hidden="true">
                <label htmlFor="website">ウェブサイト</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                  inert
                />
              </div>

              {TURNSTILE_SITE_KEY && (
                <div className="turnstile-wrap">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={setTurnstileToken}
                    onExpire={() => setTurnstileToken('')}
                    onError={() => setTurnstileToken('')}
                  />
                </div>
              )}

              {submitMessage && (
                <div className={`submit-message ${submitStatus}`}>
                  {submitMessage}
                </div>
              )}

              <button
                type="submit"
                className="btn-primary submit-button"
                disabled={isSubmitting || (Boolean(TURNSTILE_SITE_KEY) && !turnstileToken)}
              >
                {isSubmitting ? '送信中...' : (
                  <>
                    送信 <Send size={20} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
